import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.string().uuid();

/**
 * Discount status enum matching backend model
 */
export const DiscountStatusSchema = z.enum([
  "standard_price",
  "approved_discount",
  "unapproved_discount",
  "no_list_price",
  "customer_specific_discount_pending",
  "discount_pending_approval",
]);

/**
 * Discount info schema (for approved discounts)
 */
export const DiscountInfoSchema = z.object({
  id: UUIDSchema,
  discountPrice: z.number(),
  validUntil: z.coerce.date().nullable().optional(),
  sku: z.string(),
  attachment: z.string().nullable().optional(),
  /** The target type of the matched discount scope (SKU, PRODUCT_CATEGORY, PRODUCT_LINE, etc.) */
  scopeTargetType: z.string().nullable().optional(),
  /** The target value (e.g. category name "PARTS", product line "CIJ") */
  scopeTargetValue: z.string().nullable().optional(),
  /** The reason/notes entered when the discount was created */
  explanation: z.string().nullable().optional(),
  /**
   * External code (e.g. SPR) the discount was approved under. Surfaced on
   * line-item discount popovers so users can compare it against the SPR
   * the customer cited on the PO.
   */
  referenceCode: z.string().nullable().optional(),
});

/**
 * Price discrepancy schema
 */
export const PriceDiscrepancySchema = z.object({
  expectedPrice: z.number(),
  actualPrice: z.number(),
  difference: z.number(),
});

/**
 * List price info schema (from ERP)
 */
export const ListPriceInfoSchema = z.object({
  price: z.number(),
  currencyCode: z.string(),
  priceListName: z.string(),
});

/**
 * End customer info schema (for customer-specific discounts)
 */
export const EndCustomerInfoSchema = z.object({
  name: z.string(),
  discountScopeId: UUIDSchema,
});

/**
 * Pending approval info schema (for discounts awaiting multi-level approval)
 */
export const PendingApprovalInfoSchema = z.object({
  currentLevel: z.number(),
  requiredLevel: z.number(),
});

/**
 * Quantity-cap state for the matched discount. Populated when the validator
 * receives line-item quantities and the discount has a non-null maxQuantity.
 */
export const QuantityCapInfoSchema = z.object({
  remaining: z.number(),
  requested: z.number(),
  exceeded: z.boolean(),
});

/**
 * Discount validation result schema
 */
export const DiscountValidationResultSchema = z.object({
  status: DiscountStatusSchema,
  poUnitPrice: z.number(),
  discount: DiscountInfoSchema.nullable().optional(),
  discrepancy: PriceDiscrepancySchema.nullable().optional(),
  listPrice: ListPriceInfoSchema.nullable().optional(),
  endCustomer: EndCustomerInfoSchema.nullable().optional(),
  pendingApproval: PendingApprovalInfoSchema.nullable().optional(),
  quantityCap: QuantityCapInfoSchema.nullable().optional(),
});

/**
 * Validate discounts input schema
 */
export const ValidateDiscountsInputSchema = z.object({
  stagedPurchaseOrderId: UUIDSchema,
});

/**
 * Validate discounts response schema
 */
export const ValidateDiscountsResponseSchema = z.object({
  results: z.record(z.string(), DiscountValidationResultSchema),
});

/**
 * Customer-specific discount verification status
 */
export const CustomerSpecificDiscountVerificationStatusSchema = z.enum([
  "PENDING_VERIFICATION",
  "VERIFIED",
  "VERIFICATION_REQUESTED",
  "WAIVED",
]);

/**
 * Update discount verification input schema
 */
export const UpdateDiscountVerificationInputSchema = z.object({
  stagedPurchaseOrderId: UUIDSchema,
  lineItemIndex: z.number().int().min(0),
  status: z.enum(["VERIFIED", "VERIFICATION_REQUESTED", "WAIVED"]),
  notes: z.string().optional(),
});

/**
 * Update discount verification response schema
 */
export const UpdateDiscountVerificationResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Validate a single line item's discount (used after creating a discount
 * from the draft modal to avoid re-validating every line item).
 */
export const ValidateSingleDiscountInputSchema = z.object({
  stagedPurchaseOrderId: UUIDSchema,
  productVariantId: UUIDSchema,
});

export const ValidateSingleDiscountResponseSchema = z.object({
  result: DiscountValidationResultSchema,
});

/**
 * Review pending 860 changes input schema
 */
export const ReviewPendingChangesInputSchema = z.object({
  stagedPurchaseOrderId: UUIDSchema,
  lineItemDecisions: z.array(
    z.object({
      index: z.number().int().min(0),
      decision: z.enum(["approved", "denied"]),
    }),
  ),
  headerDecisions: z.array(
    z.object({
      index: z.number().int().min(0),
      decision: z.enum(["approved", "denied"]),
    }),
  ),
});

/**
 * Review pending 860 changes response schema
 */
export const ReviewPendingChangesResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Sales Order Drafts contracts
 */
export const salesOrderDraftsContracts = {
  validateDiscounts: oc
    .input(ValidateDiscountsInputSchema)
    .output(ValidateDiscountsResponseSchema)
    .route({
      summary: "Validate discounts on a staged purchase order",
      description:
        "Checks each line's price on a staged customer purchase order against its discount or list price, returning per-line results; approved orders return the discounts actually applied.",
      method: "POST",
    }),
  validateSingleDiscount: oc
    .input(ValidateSingleDiscountInputSchema)
    .output(ValidateSingleDiscountResponseSchema)
    .route({
      summary: "Validate one line's discount on a staged order",
      description:
        "Checks a single product line's purchase-order price on a staged customer order against its discount or list price, after a discount is created. Read-only.",
      method: "POST",
    }),
  updateDiscountVerification: oc
    .input(UpdateDiscountVerificationInputSchema)
    .output(UpdateDiscountVerificationResponseSchema)
    .route({
      summary: "Verify a customer-specific discount on a line",
      description:
        "Marks a staged order line's customer-specific discount as verified, verification requested, or waived, with optional notes, recording who verified it and when.",
      method: "PATCH",
    }),
  reviewPendingChanges: oc
    .input(ReviewPendingChangesInputSchema)
    .output(ReviewPendingChangesResponseSchema)
    .route({
      summary: "Approve or deny purchase order change requests",
      description:
        "Applies approve/deny decisions to a staged order's pending customer change requests: updates, adds, or removes sales order lines, marks the order approved, and re-runs product matching.",
      method: "POST",
    }),
} as const;

// Type exports
export type DiscountStatus = z.infer<typeof DiscountStatusSchema>;
export type DiscountInfo = z.infer<typeof DiscountInfoSchema>;
export type PriceDiscrepancy = z.infer<typeof PriceDiscrepancySchema>;
export type ListPriceInfo = z.infer<typeof ListPriceInfoSchema>;
export type EndCustomerInfo = z.infer<typeof EndCustomerInfoSchema>;
export type PendingApprovalInfo = z.infer<typeof PendingApprovalInfoSchema>;
export type DiscountValidationResult = z.infer<
  typeof DiscountValidationResultSchema
>;
export type ValidateDiscountsInput = z.infer<
  typeof ValidateDiscountsInputSchema
>;
export type ValidateDiscountsResponse = z.infer<
  typeof ValidateDiscountsResponseSchema
>;
export type CustomerSpecificDiscountVerificationStatus = z.infer<
  typeof CustomerSpecificDiscountVerificationStatusSchema
>;
export type UpdateDiscountVerificationInput = z.infer<
  typeof UpdateDiscountVerificationInputSchema
>;
export type UpdateDiscountVerificationResponse = z.infer<
  typeof UpdateDiscountVerificationResponseSchema
>;
export type ValidateSingleDiscountInput = z.infer<
  typeof ValidateSingleDiscountInputSchema
>;
export type ValidateSingleDiscountResponse = z.infer<
  typeof ValidateSingleDiscountResponseSchema
>;
export type ReviewPendingChangesInput = z.infer<
  typeof ReviewPendingChangesInputSchema
>;
export type ReviewPendingChangesResponse = z.infer<
  typeof ReviewPendingChangesResponseSchema
>;

/**
 * DiscountStatus constants for runtime comparison.
 * Use these instead of string literals for type safety.
 */
export const DiscountStatus = {
  STANDARD_PRICE: "standard_price",
  APPROVED_DISCOUNT: "approved_discount",
  UNAPPROVED_DISCOUNT: "unapproved_discount",
  NO_LIST_PRICE: "no_list_price",
  CUSTOMER_SPECIFIC_DISCOUNT_PENDING: "customer_specific_discount_pending",
  DISCOUNT_PENDING_APPROVAL: "discount_pending_approval",
} as const;
